/* ============================================================================
   Geo bucketing helper, S2 cell L17 equivalent (~80m hex)
   ----------------------------------------------------------------------------
   We never store raw lat/long. Before any record leaves the device, the
   client buckets the coordinate to a low-precision token. The on-disk
   precision target is S2 cell level 17, ~80 metres across.

   Implementation note, this module ships a quantised-geohash bucket that
   approximates an L17 cell at the equator (each cell ~75m × ~75m). It is
   a faithful drop-in for the lake's privacy contract, but the cell IDs
   are not interoperable with Google's S2 library. When we vendor
   `s2-geometry-javascript` in a follow-up chunk, swap toGeoBucketL17 to
   delegate to it, the column name and contract stay the same.
   ============================================================================ */

const PRECISION_BITS_PER_AXIS = 22;        // ~75m at the equator (1.0 / 2**22 deg ≈ 0.0027m, 360deg / 2^22 ≈ 75m)
const GEO_BUCKET_VERSION = 1;

/**
 * Convert a raw lat/long to a privacy-safe bucket id (~80m square).
 * Returns null when coords are missing or out of range, so the caller can
 * decide to drop the geo column entirely on that record.
 */
export function toGeoBucketL17(lat: number | null | undefined, lng: number | null | undefined): string | null {
  if (lat == null || lng == null) return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90) return null;
  if (lng < -180 || lng > 180) return null;

  // Map lat to [0, 1], lng to [0, 1], quantise to PRECISION_BITS_PER_AXIS.
  const latNorm = (lat + 90) / 180;
  const lngNorm = (lng + 180) / 360;
  const scale = 1 << PRECISION_BITS_PER_AXIS;
  const latBin = Math.min(scale - 1, Math.floor(latNorm * scale));
  const lngBin = Math.min(scale - 1, Math.floor(lngNorm * scale));

  // Encode as base36 to stay short and URL-safe.
  return `g${GEO_BUCKET_VERSION}_${latBin.toString(36)}_${lngBin.toString(36)}`;
}


